import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { User, Mail, Lock, Hash, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TERMS_VERSION } from '../constants/terms';
import { callReferral, clearAttribution, getAttribution, listenProgram, ReferralProgram } from '../firebase/referrals';

interface AuthFormProps {
  /** Called after a successful sign-in or account creation, with which one happened. */
  onAuthSuccess: (mode: 'login' | 'signup') => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ onAuthSuccess }) => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    handle: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [referralTerms, setReferralTerms] = useState<ReferralProgram | null>(null);
  const [joinReferrals, setJoinReferrals] = useState(false);
  useEffect(() => {
    if (!getAttribution()) return;
    return listenProgram(setReferralTerms, () => setReferralTerms(null));
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Auto-add @ symbol for handle input
    if (name === 'handle') {
      let processedValue = value;
      // Remove any existing @ symbols
      processedValue = processedValue.replace(/@/g, '');
      // Add @ at the beginning
      processedValue = '@' + processedValue;
      setFormData(prev => ({ ...prev, [name]: processedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const checkHandleAvailability = async (handle: string): Promise<boolean> => {
    try {
      console.log('Checking handle:', handle.toLowerCase());
      const handleDoc = await getDoc(doc(db, 'handles', handle.toLowerCase()));
      const isAvailable = !handleDoc.exists();
      console.log('Handle available:', isAvailable);
      return isAvailable;
    } catch (error) {
      console.error('Error checking handle:', error);
      // Return true on error to allow signup attempt
      return true;
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (formData.handle.length < 2) {
        setError(t('errors.handleTooShort'));
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError(t('errors.passwordsDontMatch'));
        setLoading(false);
        return;
      }

      if (!hasAcceptedTerms) {
        setError('Please accept the Terms of Service before continuing.');
        setLoading(false);
        return;
      }

      // Check handle availability
      console.log('Checking handle availability for:', formData.handle);
      const isHandleAvailable = await checkHandleAvailability(formData.handle);
      console.log('Handle availability result:', isHandleAvailable);
      if (!isHandleAvailable) {
        setError(t('errors.handleTaken'));
        setLoading(false);
        return;
      }

      // Create user
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // Create user profile. Kinship group and postal code are intentionally
      // left empty here: new members choose their kin on the Find Your Kin page
      // once they understand what the groups mean, and add their area from the
      // profile page when they're ready.
      const profileData = {
        uid: user.uid,
        email: formData.email,
        displayName: formData.displayName,
        handle: formData.handle.toLowerCase(),
        postalCode: '',
        kinshipGroup: '',
        bio: '',
        profileImageUrl: '',
        interests: [],
        tosAcceptedAt: new Date(),
        tosVersion: TERMS_VERSION,
        locationConsent: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        lastSeen: new Date()
      };

      console.log('Creating profile for user:', user.uid);
      await setDoc(doc(db, 'profiles', user.uid), profileData);
      console.log('Profile created successfully');

      // Reserve handle
      await setDoc(doc(db, 'handles', formData.handle.toLowerCase()), {
        uid: user.uid,
        createdAt: new Date()
      });

      if (joinReferrals && referralTerms) {
        try {
          await callReferral('referralJoin', { ticket: getAttribution(), termsVersion: referralTerms.version });
          clearAttribution();
        } catch {
          // Preserve the ticket and expose a retry after signup without failing account creation.
          sessionStorage.setItem('kinship.referral.retry', '1');
        }
      }
      onAuthSuccess('signup');
    } catch (error: any) {
      console.error('Signup error:', error);

      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError(t('errors.emailInUse'));
          break;
        case 'auth/invalid-email':
          setError(t('errors.invalidEmail'));
          break;
        case 'auth/weak-password':
          setError(t('errors.weakPassword'));
          break;
        case 'auth/operation-not-allowed':
          setError(t('errors.operationNotAllowed'));
          break;
        default:
          setError(error.message || t('errors.signupError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      onAuthSuccess('login');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
      setError('');
    } catch (error: any) {
      switch (error.code) {
        case 'auth/user-not-found':
          setError(t('errors.userNotFound'));
          break;
        case 'auth/invalid-email':
          setError(t('errors.invalidEmail'));
          break;
        case 'auth/too-many-requests':
          setError(t('errors.tooManyRequests'));
          break;
        default:
          setError(error.message || t('errors.resetEmailFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-accent-green rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">K</span>
          </div>
          <h2 className="text-2xl font-bold text-text-primary">
            {showPasswordReset ? t('auth.resetPassword') : (isLogin ? t('auth.welcomeBack') : t('auth.joinKinshipWay'))}
          </h2>
          <p className="text-text-secondary mt-2">
            {showPasswordReset
              ? t('auth.enterEmailReset')
              : (isLogin ? t('auth.signInToAccount') : 'Two minutes, four fields — then you’re kin.')
            }
          </p>
        </div>

        {error && (
          <div className="bg-red-900 bg-opacity-20 border border-red-500 text-red-400 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {resetSent && (
          <div className="bg-green-900 bg-opacity-20 border border-green-500 text-green-400 px-4 py-3 rounded-md mb-4">
            {t('auth.resetEmailSent')}
          </div>
        )}

        {showPasswordReset ? (
          <div>
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">{t('home.email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="w-full bg-dark-bg border border-dark-border rounded-md pl-10 pr-4 py-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                    placeholder="your@email.com"
                  />
                </div>
                <p className="text-xs text-text-muted mt-2">
                  {t('auth.enterEmailResetDesc')}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-accent-blue text-white py-2 px-4 rounded-md hover:bg-accent-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('auth.sending') : t('auth.sendResetLink')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(false);
                    setResetEmail('');
                    setResetSent(false);
                    setError('');
                  }}
                  className="flex-1 bg-dark-bg border border-dark-border text-text-primary py-2 px-4 rounded-md hover:bg-dark-bg-hover transition-colors"
                >
                  {t('auth.backToLogin')}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={isLogin ? handleLogin : handleSignup} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label className="block text-text-secondary text-sm mb-2">{t('home.displayName')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    required={!isLogin}
                    className="w-full bg-dark-bg border border-dark-border rounded-md pl-10 pr-4 py-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">{t('home.handle')}</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="text"
                    name="handle"
                    value={formData.handle}
                    onChange={handleInputChange}
                    required={!isLogin}
                    className="w-full bg-dark-bg border border-dark-border rounded-md pl-10 pr-4 py-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                    placeholder="yourhandle"
                  />
                </div>
                <p className="text-xs text-text-muted mt-1">{t('auth.chooseUniqueHandle')}</p>
              </div>
            </>
          )}

          <div>
            <label className="block text-text-secondary text-sm mb-2">{t('home.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full bg-dark-bg border border-dark-border rounded-md pl-10 pr-4 py-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-text-secondary text-sm mb-2">{t('home.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="w-full bg-dark-bg border border-dark-border rounded-md pl-10 pr-4 py-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                placeholder="••••••••"
              />
            </div>
            {isLogin && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(true);
                    setResetEmail(formData.email);
                    setError('');
                    setResetSent(false);
                  }}
                  className="text-accent-blue hover:underline text-sm"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
            )}
          </div>

          {!isLogin && (
            <div>
              <label className="block text-text-secondary text-sm mb-2">{t('home.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!isLogin}
                  className="w-full bg-dark-bg border border-dark-border rounded-md pl-10 pr-4 py-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-blue"
                  placeholder="••••••••"
                />
              </div>
            </div>
          )}

          {!isLogin && (
            <div className="flex items-start gap-3 rounded-md border border-accent-purple/30 bg-accent-purple/10 px-4 py-3 text-sm text-text-secondary">
              <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-purple" />
              <p>
                No forms about kinship groups yet — once you're inside, you'll learn what the twelve
                groups mean and choose (or spin for) yours whenever you feel ready.
              </p>
            </div>
          )}

          {!isLogin && (
            <div className="rounded-md border border-dark-border bg-dark-bg px-4 py-3 text-sm text-text-secondary">
              <p className="text-text-primary font-medium mb-2">Terms of Service</p>
              <p>
                By creating an account, you agree that user information (profile, posts, messages,
                and activity) is stored in a secured backend. If you enable location features, your
                approximate location may be saved to help you find community near you.
              </p>
              <div className="flex items-start gap-3 mt-3">
                <input
                  id="signup-terms"
                  type="checkbox"
                  checked={hasAcceptedTerms}
                  onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                  className="mt-1"
                  required
                />
                <label htmlFor="signup-terms" className="text-text-primary">
                  I agree to the{' '}
                  <Link to="/terms" className="font-semibold text-accent-blue hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="font-semibold text-accent-blue hover:underline">
                    Privacy Notice
                  </Link>
                  , including data storage and location usage.
                </label>
              </div>
            </div>
          )}

          {!isLogin && referralTerms?.enabled && <div className="rounded-md border border-accent-purple/30 p-4 text-sm text-text-secondary"><p className="font-semibold text-text-primary">You arrived with a community invitation.</p><details className="my-3"><summary className="cursor-pointer">Referral terms & eligibility · version {referralTerms.version}</summary><p className="mt-2">{referralTerms.terms}</p><p className="mt-2">{referralTerms.eligibility}</p></details><label className="flex items-start gap-3"><input type="checkbox" checked={joinReferrals} onChange={e => setJoinReferrals(e.target.checked)} /> I accept these referral terms and want to join the program. This is optional.</label></div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent-blue text-white py-2 px-4 rounded-md hover:bg-accent-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('common.loading') : (isLogin ? t('common.signIn') : t('home.createAccount'))}
          </button>
        </form>
        )}

        {!showPasswordReset && (
          <div className="text-center mt-6">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-accent-blue hover:underline text-sm"
            >
              {isLogin ? t('auth.dontHaveAccount') : t('auth.alreadyHaveAccount')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthForm;
